import { toZonedTime } from 'date-fns-tz';
import { PlaySessionMatchCompetitivenessEnum } from 'types/generated/client';
import { expandAvailabilityDays, timesForDay } from 'utils/shared/playerAvailability';
import { getTimezone } from 'utils/shared/time/getTimezone';
import { type FindFilters, playerNearestVenue, timeOfDayFromHour } from 'screens/Find/filters';
import { WEEKDAY_INDEX, WEEKDAY_TO_DAY_OF_WEEK } from 'screens/ListAvailability/utils';
import {
  HomeDateRangeEnum,
  HomeFilters,
  HomeIntentEnum,
} from 'screens/PlayerHomeV3/components/GetOnCourtSection/types';
import {
  matchesLocation,
  withinChosenRadius,
} from 'screens/PlayerHomeV3/components/TopOpportunitiesSection/sheetFilters';
import type { FindPlayer, FindPlayerRawSlot } from 'components/Find/types';

// Bar → availability listing rules, see DECISIONS-HOME-FEED.md §1.

const minutesOf = (time: string) => {
  const [hours, minutes] = time.split(':').map(Number);
  return hours * 60 + (minutes || 0);
};

/** Weekday index + minutes-of-day for `now` in the venue's zone (browser zone if unknown). */
const localNow = (now: Date, timezone?: string) => {
  const zoned = toZonedTime(now, getTimezone(timezone));
  return { day: zoned.getDay(), minutes: zoned.getHours() * 60 + zoned.getMinutes() };
};

/** Ms until this weekly slot next ends, from `now` in the venue's zone. */
const msUntilSlotEnd = (slot: FindPlayerRawSlot, now: Date, timezone?: string): number => {
  const local = localNow(now, timezone);
  const daysAhead = (WEEKDAY_INDEX[slot.dayOfWeek] - local.day + 7) % 7;
  const minutesToEnd = minutesOf(slot.endTime) - local.minutes + daysAhead * 24 * 60;
  return (minutesToEnd < 0 ? minutesToEnd + 7 * 24 * 60 : minutesToEnd) * 60 * 1000;
};

/** Ms until this weekly slot next starts (0 when it's currently running). */
const msUntilSlotStart = (slot: FindPlayerRawSlot, now: Date, timezone?: string): number => {
  const untilEnd = msUntilSlotEnd(slot, now, timezone);
  const durationMinutes = minutesOf(slot.endTime) - minutesOf(slot.startTime);
  // A slot ending past midnight has end < start; it still lasts under a day.
  const duration = (durationMinutes < 0 ? durationMinutes + 24 * 60 : durationMinutes) * 60 * 1000;
  return Math.max(untilEnd - duration, 0);
};

export const nextSlotStartMs = (player: FindPlayer, now: Date): number => {
  const slots = player.slots ?? [];
  if (slots.length === 0) return Number.POSITIVE_INFINITY;
  return Math.min(...slots.map((slot) => msUntilSlotStart(slot, now, player.timezone)));
};

const intentCompetitiveness = (
  intent: HomeIntentEnum,
): PlaySessionMatchCompetitivenessEnum | null => {
  if (intent === HomeIntentEnum.Compete) return PlaySessionMatchCompetitivenessEnum.Match;
  if (intent === HomeIntentEnum.JoinGame) return PlaySessionMatchCompetitivenessEnum.Casual;
  return null;
};

/** Does any of the player's slots fall inside my availability pattern? */
export const overlapsMyAvailability = (player: FindPlayer, sheet: FindFilters): boolean => {
  if (sheet.availabilityDays.length === 0) return true;
  const myDays = expandAvailabilityDays(sheet.availabilityDays);
  return (player.slots ?? []).some((slot) => {
    const day = WEEKDAY_TO_DAY_OF_WEEK[slot.dayOfWeek];
    if (!myDays.includes(day)) return false;
    const times = timesForDay(day, sheet.availabilityDays, sheet.timesByDay);
    return times.length === 0 || times.includes(timeOfDayFromHour(minutesOf(slot.startTime) / 60));
  });
};

const matchesPlayerSheet = (player: FindPlayer, sheet: FindFilters): boolean => {
  if (!withinChosenRadius(playerNearestVenue(player), sheet)) return false;
  if (
    !matchesLocation(
      player,
      (player.requestVenues ?? []).map((venue) => venue.id),
      sheet,
    )
  )
    return false;
  if (
    sheet.skillLevels.length &&
    !(player.skillLevel && sheet.skillLevels.includes(player.skillLevel))
  )
    return false;
  return true;
};

export const selectTopPlayers = (
  players: FindPlayer[],
  filters: HomeFilters,
  sheet?: FindFilters,
  now = new Date(),
): FindPlayer[] => {
  const competitiveness = intentCompetitiveness(filters.intent);
  const todayEnd = new Date(now);
  todayEnd.setHours(23, 59, 59, 999);
  const msLeftToday = todayEnd.getTime() - now.getTime();
  const overlapRank = (player: FindPlayer) =>
    sheet && overlapsMyAvailability(player, sheet) ? 0 : 1;

  return players
    .filter((player) => player.sport === filters.sport)
    .filter((player) => !competitiveness || player.competitiveness === competitiveness)
    .filter((player) => !sheet || matchesPlayerSheet(player, sheet))
    .filter(
      (player) =>
        filters.dateRange !== HomeDateRangeEnum.Today ||
        nextSlotStartMs(player, now) <= msLeftToday,
    )
    .sort(
      (a, b) =>
        overlapRank(a) - overlapRank(b) || nextSlotStartMs(a, now) - nextSlotStartMs(b, now),
    );
};
